/*
  Absenz- und Ferientypen verwalten.

  Beide Listen sind DS-Tabellen: eine Kopfzeile statt Feldbeschriftungen an
  jeder Zeile. Gespeichert wird beim Verlassen eines Feldes, wie im Rest des
  Dialogs — es gibt bewusst keinen Speichern-Knopf. Gelöscht wird nur, was
  nirgends verwendet wird; alles andere wandert ins Archiv, weil die Datenbank
  ein DELETE benutzter Typen ablehnt.
*/

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import AppButton from "../../design-system/components/AppButton";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppCheckbox from "../../design-system/components/AppCheckbox";
import AppChip from "../../design-system/components/AppChip";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppTable from "../../design-system/components/AppTable";
import AppText from "../../design-system/components/AppText";
import { IconAdd, IconArchive, IconDelete, IconSuccess } from "../../design-system/icons";
import { normalizeApiError } from "../shiftplan_v2/api/errors";
import {
  createInitialSetupAbsenceType,
  createInitialSetupHolidayType,
  deleteInitialSetupAbsenceType,
  deleteInitialSetupHolidayType,
  getInitialSetupTypeSettings,
  updateInitialSetupAbsenceType,
  updateInitialSetupHolidayType,
} from "./api";
import { ColorSwatchControl } from "../shiftplan_v2/features/panels/AppearanceSettingsPanel";

const ABSENCE_KIND = "absence";
const HOLIDAY_KIND = "holiday";
const DEFAULT_ABSENCE_COLOR = "#1C9A9A";
const HOLIDAY_PRESETS = [
  {
    name: "Vormittag",
    label: "Vormittag",
    payload: { name: "Vormittag", start_time: "08:00", end_time: "12:00", hours_per_day: 4 },
  },
  {
    name: "Nachmittag",
    label: "Nachmittag",
    payload: { name: "Nachmittag", start_time: "13:00", end_time: "18:00", hours_per_day: 5 },
  },
];
const ABSENCE_PRESETS = [
  { name: "Krank", label: "Krank", payload: { name: "Krank", hours_per_day: 8.4, counts_as_work_time: true } },
  { name: "Freiwunsch", label: "Freiwunsch", payload: { name: "Freiwunsch", hours_per_day: 8.4, counts_as_work_time: true } },
  { name: "Militär", label: "Militär", payload: { name: "Militär", hours_per_day: 8.4, counts_as_work_time: true } },
];

function asText(value) {
  return String(value ?? "").trim();
}

function hoursToField(value) {
  if (value === null || value === undefined || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : "";
}

function fieldToHours(value) {
  const raw = asText(value).replace(",", ".");
  if (!raw) return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) return null;
  return Math.round(parsed * 100) / 100;
}

function usageLabel(count) {
  const value = Number(count) || 0;
  return value === 1 ? "1 Eintrag" : `${value} Einträge`;
}

function timeToMinutes(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(asText(value));
  if (!match) return null;
  const minutes = Number(match[1]) * 60 + Number(match[2]);
  return Number.isFinite(minutes) ? minutes : null;
}

/*
  Stunden aus der Zeitspanne. 00:00–23:59 ergibt 23.98 – das ist gewollt: wer
  einen glatten Tageswert will, trägt ihn bei Stunden/Tag ein.
*/
function hoursFromSpan(startTime, endTime) {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  if (start === null || end === null || end <= start) return null;
  return Math.round(((end - start) / 60) * 100) / 100;
}

/*
  Ein Ferientyp wird entweder von der Zeitspanne oder vom Stundenwert bestimmt.
  Statt einer zusätzlichen Spalte leiten wir das aus den Daten ab: deckt sich
  der Stundenwert mit der Spanne, war die Zeit der Ausgangspunkt.
*/
function isTimeDriven(row) {
  const span = hoursFromSpan(row?.start_time, row?.end_time);
  if (span === null) return false;
  const hours = Number(row?.hours_per_day);
  return Number.isFinite(hours) && Math.abs(hours - span) < 0.005;
}

/*
  Die Typenverwaltung liefert dieselbe Nutzlast wie der Bootstrap, zusätzlich
  die Archive und die Verwendungszahlen.
*/
function readTypeLists(payload) {
  return {
    absences: Array.isArray(payload?.absence_list) ? payload.absence_list : [],
    absenceArchive: Array.isArray(payload?.absence_archive_list) ? payload.absence_archive_list : [],
    holidays: Array.isArray(payload?.user_holiday_type_list) ? payload.user_holiday_type_list : [],
    holidayArchive: Array.isArray(payload?.user_holiday_type_archive_list)
      ? payload.user_holiday_type_archive_list
      : [],
  };
}

/*
  Feld, das seinen Wert erst beim Verlassen abschickt. Solange man tippt, gilt
  der lokale Stand; kommt von aussen ein neuer Wert, zieht das Feld nach.
*/
function CommitField({
  value,
  disabled,
  muted = false,
  ariaLabel,
  type = "text",
  use24HourText = false,
  trailingText,
  placeholder,
  onCommit,
}) {
  const [draft, setDraft] = useState(value);
  const committedRef = useRef(value);

  useEffect(() => {
    committedRef.current = value;
    setDraft(value);
  }, [value]);

  return (
    <AppInput
      size="sm"
      type={type === "time" && use24HourText ? "text" : type}
      inputMode={type === "time" && use24HourText ? "numeric" : undefined}
      maxLength={type === "time" && use24HourText ? 5 : undefined}
      value={draft}
      disabled={disabled}
      className={muted ? "spv2-type-input-muted" : ""}
      placeholder={type === "time" && use24HourText ? "HH:MM" : placeholder}
      trailingText={trailingText}
      aria-label={ariaLabel}
      onChange={(event) => {
        const nextValue = event.target.value;
        if (type !== "time" || !use24HourText) {
          setDraft(nextValue);
          return;
        }
        const cleaned = nextValue.replace(/[^\d:]/g, "").slice(0, 5);
        if (/^\d{3}$/.test(cleaned)) {
          setDraft(`0${cleaned[0]}:${cleaned.slice(1)}`);
          return;
        }
        setDraft(/^\d{4}$/.test(cleaned) ? `${cleaned.slice(0, 2)}:${cleaned.slice(2)}` : cleaned);
      }}
      onBlur={() => {
        if (draft === committedRef.current) return;
        onCommit(draft, () => setDraft(committedRef.current));
      }}
    />
  );
}

/*
  Kein Tooltip für die Erklärungen: der Einstellungen-Dialog liegt auf z-index
  3300, das Design System hält Tooltips auf 1600 – sie kämen dahinter zu liegen.
  Die Hinweise stehen darum sichtbar in der Tabelle oder im Kopf der Gruppe.

  Auch kein Schloss-Symbol: was gesperrt ist, ist ausgegraut – das gilt für das
  Namensfeld von "Freiwunsch" ebenso wie für die Zeiten des Standard-Ferientyps.
  Ein zusätzliches Symbol nur an einer der beiden Stellen las sich willkürlich.
*/
function TypeNameCell({ row, locked, busy, onCommit }) {
  return (
    <span className="spv2-type-name-cell">
      <CommitField
        value={row.name || ""}
        disabled={locked || busy}
        ariaLabel={`Name von ${row.name}`}
        onCommit={onCommit}
      />
      {row.is_system_default ? (
        <AppChip variant="muted" appearance="semiFilled" size="sm" className="spv2-type-system-mark">
          Standard
        </AppChip>
      ) : null}
    </span>
  );
}

function UsageCell({ row }) {
  const count = Number(row.usage_count) || 0;

  return (
    <AppText
      as="span"
      variant="body-3"
      tone={count ? "primary" : "tertiary"}
      className="spv2-type-usage"
    >
      {count ? usageLabel(count) : "nicht verwendet"}
    </AppText>
  );
}

function RemoveButton({ row, busy, onClick }) {
  const willArchive = Boolean(row.usage_count);
  const locked = Boolean(row.is_system_default);
  const label = locked
    ? `${row.name} ist geschützt`
    : willArchive
      ? `${row.name} archivieren`
      : `${row.name} löschen`;

  return (
    <AppButton
      variant="neutral"
      size="sm"
      iconOnly
      disabled={busy || locked}
      aria-label={label}
      onClick={onClick}
    >
      {willArchive ? <IconArchive sx={{ fontSize: 16 }} /> : <IconDelete sx={{ fontSize: 16 }} />}
    </AppButton>
  );
}

function TypePresetButtons({ presets, rows, busy, onSelect }) {
  const existingNames = new Set((rows || []).map((row) => asText(row.name).toLowerCase()));

  return (
    <Box className="spv2-type-presets">
      {presets.map((preset) => {
        const exists = existingNames.has(preset.name.toLowerCase());
        return (
          <AppButton
            key={preset.name}
            variant="outlined"
            size="sm"
            className="spv2-type-preset"
            disabled={busy || exists}
            onClick={() => onSelect(preset)}
          >
            {exists ? <IconSuccess sx={{ fontSize: 17 }} /> : "+"} {preset.label}
          </AppButton>
        );
      })}
    </Box>
  );
}

function TypeGroup({ title, description, addLabel, busy, children, onAdd, presets = [], rows = [], onPreset, showHeader = true }) {
  return (
    <Box className="spv2-type-group">
      {showHeader ? (
        <Box className="spv2-type-group-head">
          <span className="spv2-type-group-copy">
            <AppText as="h3" variant="heading-4" className="spv2-type-group-title">
              {title}
            </AppText>
            <AppText as="p" variant="body-3" tone="secondary" className="spv2-type-group-description">
              {description}
            </AppText>
          </span>
          <AppButton variant="neutral" size="sm" disabled={busy} onClick={onAdd}>
            <IconAdd />
            {addLabel}
          </AppButton>
        </Box>
      ) : null}
      {presets.length ? (
        <TypePresetButtons presets={presets} rows={rows} busy={busy} onSelect={onPreset} />
      ) : null}
      {children}
    </Box>
  );
}

function ArchiveList({ rows, busy, onRestore }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;

  return (
    <Box className="spv2-type-archive">
      <AppButton
        variant="muted"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <IconArchive />
        {open ? "Archiv ausblenden" : `Archiv (${rows.length})`}
      </AppButton>

      {open ? (
        <Box className="spv2-type-archive-list">
          {rows.map((row) => (
            <Box key={row.id} className="spv2-type-archive-row">
              <AppText as="span" variant="body-2" truncate className="spv2-type-archive-name">
                {row.name}
              </AppText>
              <AppText as="span" variant="body-3" tone="tertiary">
                {row.usage_count ? usageLabel(row.usage_count) : "nicht verwendet"}
              </AppText>
              <AppButton variant="neutral" size="sm" disabled={busy} onClick={() => onRestore(row)}>
                Wieder aktivieren
              </AppButton>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export function TypeSettingsContent({
  active,
  onTypesChanged,
  visibleKind = "all",
  showPresets = false,
  cardHeader = null,
  onboarding = false,
}) {
  const [lists, setLists] = useState({
    absences: [],
    absenceArchive: [],
    holidays: [],
    holidayArchive: [],
  });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const loadedRef = useRef(false);

  const applyPayload = useCallback(
    (payload) => {
      setLists(readTypeLists(payload));
      onTypesChanged?.(payload);
    },
    [onTypesChanged]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      applyPayload(await getInitialSetupTypeSettings());
      loadedRef.current = true;
    } catch (requestError) {
      setError(normalizeApiError(requestError).message);
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    if (!active || loadedRef.current) return;
    load();
  }, [active, load]);

  const run = useCallback(
    async (request) => {
      setBusy(true);
      setError("");
      try {
        applyPayload(await request());
        return true;
      } catch (requestError) {
        const message = normalizeApiError(requestError).message;
        // Der Server bleibt die Wahrheit: nach einem Fehler neu laden, damit
        // die Zeile nicht einen Wert zeigt, der nie gespeichert wurde. Die
        // Meldung kommt erst danach, weil das Laden den Fehler zurücksetzt.
        await load();
        setError(message);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [applyPayload, load]
  );

  const changeAbsence = useCallback(
    (row, patch) => run(() => updateInitialSetupAbsenceType(row.id, patch)),
    [run]
  );
  const changeHoliday = useCallback(
    (row, patch) => run(() => updateInitialSetupHolidayType(row.id, patch)),
    [run]
  );

  /*
    Zeitspanne ändern heisst zugleich: die Stunden ergeben sich daraus. Der
    berechnete Wert geht mit, damit Anzeige und gespeicherter Wert nicht
    auseinanderlaufen — er ist ja das, was beim Erfassen gebucht wird.
  */
  const commitHolidaySpan = useCallback(
    (row, patch) => {
      const next = {
        start_time: row.start_time || "00:00",
        end_time: row.end_time || "23:59",
        ...patch,
      };
      const hours = hoursFromSpan(next.start_time, next.end_time);
      return changeHoliday(row, hours === null ? next : { ...next, hours_per_day: hours });
    },
    [changeHoliday]
  );

  const addAbsence = useCallback(
    () => run(() => createInitialSetupAbsenceType({ name: "Neuer Absenztyp", color_hex: DEFAULT_ABSENCE_COLOR })),
    [run]
  );
  const addHoliday = useCallback(
    () => run(() => createInitialSetupHolidayType({ name: "Neuer Ferientyp", hours_per_day: 8.4 })),
    [run]
  );

  const addAbsencePreset = useCallback(
    (preset) => {
      const existing = lists.absences.find(
        (row) => asText(row.name).toLowerCase() === preset.name.toLowerCase()
      );
      if (!existing) return run(() => createInitialSetupAbsenceType(preset.payload));
      const patch = { ...preset.payload };
      delete patch.name;
      return run(() => updateInitialSetupAbsenceType(existing.id, patch));
    },
    [lists.absences, run]
  );
  const addHolidayPreset = useCallback(
    (preset) => {
      const existing = lists.holidays.find(
        (row) => asText(row.name).toLowerCase() === preset.name.toLowerCase()
      );
      if (!existing) return run(() => createInitialSetupHolidayType(preset.payload));
      const patch = { ...preset.payload };
      delete patch.name;
      return run(() => updateInitialSetupHolidayType(existing.id, patch));
    },
    [lists.holidays, run]
  );

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    const { kind, row } = pendingDelete;
    setPendingDelete(null);
    await run(() => (
      kind === ABSENCE_KIND
        ? deleteInitialSetupAbsenceType(row.id)
        : deleteInitialSetupHolidayType(row.id)
    ));
  }, [pendingDelete, run]);

  const absenceColumns = useMemo(
    () => [
      {
        key: "color",
        label: "",
        width: 76,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <ColorSwatchControl
            type="absence"
            item={{ id: row.id, title: row.name }}
            color={row.color_hex || DEFAULT_ABSENCE_COLOR}
            saving={busy}
            onColorChange={(_type, item, color) => changeAbsence(item, { color_hex: color })}
          />
        ),
      },
      {
        key: "name",
        label: "Name",
        minWidth: 240,
        render: (_value, row) => (
          <TypeNameCell
            row={row}
            locked={Boolean(row.is_system_default)}
            busy={busy}
            onCommit={(next, revert) => {
              if (!asText(next)) return revert();
              return changeAbsence(row, { name: asText(next) });
            }}
          />
        ),
      },
      {
        key: "hours_per_day",
        label: "Stunden/Tag",
        width: 124,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="number"
            value={hoursToField(row.hours_per_day)}
            disabled={busy || row.counts_as_work_time === false}
            placeholder={onboarding ? undefined : "Tagessoll"}
            trailingText="h"
            ariaLabel={`Stunden pro Tag von ${row.name}`}
            onCommit={(next) => changeAbsence(row, { hours_per_day: fieldToHours(next) })}
          />
        ),
      },
      {
        key: "counts_as_work_time",
        label: "Arbeitszeit",
        width: 96,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <AppCheckbox
            className={onboarding ? "spv2-type-checkbox" : ""}
            checked={row.counts_as_work_time !== false}
            disabled={busy}
            aria-label={`${row.name} zählt als Arbeitszeit`}
            onChange={(event) => changeAbsence(row, { counts_as_work_time: event.target.checked })}
          />
        ),
      },
      {
        key: "usage_count",
        label: "Verwendung",
        // Feste Breite: die freie Breite soll dem Namen zugutekommen.
        width: 140,
        align: onboarding ? "center" : undefined,
        sortable: false,
        render: (_value, row) => <UsageCell row={row} />,
      },
      {
        key: "actions",
        label: "",
        width: 60,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <RemoveButton
            row={row}
            busy={busy}
            onClick={() => setPendingDelete({ kind: ABSENCE_KIND, row })}
          />
        ),
      },
    ],
    [busy, changeAbsence, onboarding]
  );

  const holidayColumns = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        minWidth: 240,
        render: (_value, row) => (
          <TypeNameCell
            row={row}
            // Der Standard-Ferientyp wird über is_system_default gefunden,
            // Umbenennen ist darum gefahrlos.
            locked={false}
            busy={busy}
            onCommit={(next, revert) => {
              if (!asText(next)) return revert();
              return changeHoliday(row, { name: asText(next) });
            }}
          />
        ),
      },
      {
        key: "hours_per_day",
        label: "Stunden/Tag",
        width: 124,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="number"
            value={hoursToField(row.hours_per_day)}
            disabled={busy}
            // Kommt der Wert aus der Zeitspanne, ist er nur abgeleitet.
            muted={isTimeDriven(row)}
            trailingText="h"
            ariaLabel={`Stunden pro Tag von ${row.name}`}
            onCommit={(next, revert) => {
              const hours = fieldToHours(next);
              if (hours === null) return revert();
              return changeHoliday(row, { hours_per_day: hours });
            }}
          />
        ),
      },
      {
        key: "start_time",
        label: "Von",
        width: 132,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="time"
            use24HourText={onboarding}
            value={row.start_time || "00:00"}
            disabled={busy || Boolean(row.is_system_default)}
            muted={!isTimeDriven(row)}
            ariaLabel={`Startzeit von ${row.name}`}
            onCommit={(next) => commitHolidaySpan(row, { start_time: asText(next) || "00:00" })}
          />
        ),
      },
      {
        key: "end_time",
        label: "Bis",
        width: 132,
        sortable: false,
        render: (_value, row) => (
          <CommitField
            type="time"
            use24HourText={onboarding}
            value={row.end_time || "23:59"}
            disabled={busy || Boolean(row.is_system_default)}
            muted={!isTimeDriven(row)}
            ariaLabel={`Endzeit von ${row.name}`}
            onCommit={(next) => commitHolidaySpan(row, { end_time: asText(next) || "23:59" })}
          />
        ),
      },
      {
        key: "usage_count",
        label: "Verwendung",
        // Feste Breite: die freie Breite soll dem Namen zugutekommen.
        width: 140,
        align: onboarding ? "center" : undefined,
        sortable: false,
        render: (_value, row) => <UsageCell row={row} />,
      },
      {
        key: "actions",
        label: "",
        width: 60,
        align: "center",
        sortable: false,
        render: (_value, row) => (
          <RemoveButton
            row={row}
            busy={busy}
            onClick={() => setPendingDelete({ kind: HOLIDAY_KIND, row })}
          />
        ),
      },
    ],
    [busy, changeHoliday, commitHolidaySpan, onboarding]
  );

  const markSystemRow = useCallback(
    (row) => (row.is_system_default ? "spv2-type-row--system" : ""),
    []
  );

  const willArchive = Boolean(pendingDelete?.row?.usage_count);
  const isInitialLoading = loading && !loadedRef.current;
  const usesCardHeader = Boolean(cardHeader && visibleKind !== "all");
  const addVisibleType = visibleKind === HOLIDAY_KIND ? addHoliday : addAbsence;
  const visibleAddLabel = cardHeader?.addLabel || (visibleKind === HOLIDAY_KIND ? "Ferientyp" : "Absenztyp");

  const content = (
    <Box className="spv2-type-settings">
      {/* Ladeanzeige des Design Systems, im Bereich statt über der ganzen Seite. */}
      <AppLoadingOverlay
        open={isInitialLoading}
        scope="contained"
        blocking={false}
        label="Typen werden geladen"
      />
      {error ? <Alert severity="error">{error}</Alert> : null}

      {visibleKind === "all" || visibleKind === ABSENCE_KIND ? (
        <TypeGroup
          title="Absenztypen"
          description="Ohne Stundenwert zählt das Tagessoll der Person. „Standard“ kennzeichnet den Typ für Freiwünsche — er kann weder umbenannt noch entfernt werden."
          addLabel="Absenztyp"
          busy={busy}
          onAdd={addAbsence}
          presets={showPresets ? ABSENCE_PRESETS : []}
          rows={lists.absences}
          onPreset={addAbsencePreset}
          showHeader={!usesCardHeader}
        >
          <AppTable
            className="spv2-type-table"
            density="compact"
            columns={absenceColumns}
            rows={lists.absences}
            getRowClassName={markSystemRow}
            showFooter={false}
            emptyState="Noch keine Absenztypen angelegt."
          />
          <ArchiveList
            rows={lists.absenceArchive}
            busy={busy}
            onRestore={(row) => changeAbsence(row, { is_active: true })}
          />
        </TypeGroup>
      ) : null}

      {visibleKind === "all" || visibleKind === HOLIDAY_KIND ? (
        <TypeGroup
          title="Ferientypen"
          description="Entweder Stunden/Tag eintragen oder eine Zeitspanne — der jeweils andere Wert ist dann nur noch abgeleitet und wird blass dargestellt. Der Standardtyp deckt den ganzen Tag ab und bleibt immer wählbar."
          addLabel="Ferientyp"
          busy={busy}
          onAdd={addHoliday}
          presets={showPresets ? HOLIDAY_PRESETS : []}
          rows={lists.holidays}
          onPreset={addHolidayPreset}
          showHeader={!usesCardHeader}
        >
          <AppTable
            className="spv2-type-table"
            density="compact"
            columns={holidayColumns}
            rows={lists.holidays}
            getRowClassName={markSystemRow}
            showFooter={false}
            emptyState="Noch keine Ferientypen angelegt."
          />
          <ArchiveList
            rows={lists.holidayArchive}
            busy={busy}
            onRestore={(row) => changeHoliday(row, { is_active: true })}
          />
        </TypeGroup>
      ) : null}

      {/*
        Der DS-Dialog liegt auf z-index 1200, der Einstellungen-Dialog auf 3300 —
        die Rückfrage käme sonst dahinter zu liegen. Die Abweichung steht lokal
        in panels.css, das Design System bleibt unverändert.
      */}
      <AppConfirmDialog
        rootClassName="spv2-type-confirm-root"
        open={Boolean(pendingDelete)}
        title={willArchive ? `„${pendingDelete?.row?.name}“ archivieren?` : `„${pendingDelete?.row?.name}“ löschen?`}
        description={
          willArchive
            ? `Wird in ${usageLabel(pendingDelete?.row?.usage_count)} verwendet und kann darum nicht gelöscht werden. Die Einträge bleiben bestehen, der Typ ist neu aber nicht mehr wählbar.`
            : "Wird aktuell nicht verwendet und kann vollständig entfernt werden."
        }
        confirmLabel={willArchive ? "Archivieren" : "Löschen"}
        cancelLabel="Abbrechen"
        confirmTone={willArchive ? "default" : "error"}
        icon={willArchive ? <IconArchive /> : <IconDelete />}
        onConfirm={confirmDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Box>
  );

  if (usesCardHeader) {
    return (
      <>
        <AppCardHeader
          title={cardHeader.title}
          visual={cardHeader.visual}
          action={(
            <AppButton variant="primary" size="sm" disabled={busy} onClick={addVisibleType}>
              <IconAdd />
              {visibleAddLabel}
            </AppButton>
          )}
        />
        <AppCardBody>{content}</AppCardBody>
      </>
    );
  }

  return content;
}

export default TypeSettingsContent;
